/**
 * Sprint-11 — Read-only binder view for external reviewers.
 *
 * URL shape: /external/binders/:binderId
 *
 * Auth model:
 *   - The page reads the scope-bounded session JWT from localStorage
 *     (`dreamit2.external.session` — keyed separately from the internal
 *     `dreamit2:session` so a coordinator's staff session in the same
 *     browser is untouched).
 *   - If no token is present, we redirect back to /external/grant.
 *   - On 401/403 from any call, we clear the token and surface a "your
 *     session has expired" screen with a re-start button.
 *
 * Layout:
 *   - Header (top): "External review — {short binder id}", scope badge,
 *     valid_until pill, Sign-out button.
 *   - Disclaimer banner: external read-only view + PHI-redaction note.
 *   - Body: simple folder tree (left) + read-only document table (right).
 *
 * This page deliberately re-implements (rather than reuses) the
 * BinderFolderTree + DocumentSlotList components so the read paths only
 * call `externalAccess.*` — never the coordinator-side `binder.*` endpoints,
 * which would 401 for a reviewer.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExternalDocumentViewerModal } from '@/components/external/ExternalDocumentViewerModal';
import { AlertTriangle, ChevronDown, ChevronRight, FileText, FolderArchive, Info, Loader2, LogOut, ShieldAlert } from 'lucide-react';
import {
  ApiError,
  externalAccess,
  type BinderFolderRow,
  type ExternalDocumentVersionRow,
} from '@/api/client';
import { fmtDateTime } from '@/lib/dates.js';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';

const EXTERNAL_SESSION_KEY = 'dreamit2.external.session';

interface PersistedExternalSession {
  session_token: string;
  grant_id: string;
  scope_binder_id: string;
}

function readSession(): PersistedExternalSession | null {
  try {
    const raw = localStorage.getItem(EXTERNAL_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedExternalSession>;
    if (
      typeof parsed.session_token === 'string' &&
      typeof parsed.grant_id === 'string' &&
      typeof parsed.scope_binder_id === 'string'
    ) {
      return parsed as PersistedExternalSession;
    }
    return null;
  } catch {
    return null;
  }
}

interface DecodedSessionClaims {
  scope?: string;
  granted_to_email?: string;
  granted_to_role?: string;
  exp?: number;
}

function decodeClaims(jwt: string): DecodedSessionClaims | null {
  try {
    const parts = jwt.split('.');
    if (parts.length < 2) return null;
    const payload = parts[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    return JSON.parse(atob(padded)) as DecodedSessionClaims;
  } catch {
    return null;
  }
}

function scopeLabel(scope: string | undefined): string {
  switch (scope) {
    case 'view_full_binder':
      return 'Full binder';
    case 'view_pii_redacted':
      return 'PHI redacted';
    case 'view_specific_folders':
      return 'Specific folders';
    default:
      return scope ?? 'Scoped view';
  }
}

function folderDisplayName(f: BinderFolderRow): string {
  return f.display_name_override ?? f.subartifact_label ?? f.artifact_type;
}

export default function ExternalBinderPage() {
  const { binderId = '' } = useParams<{ binderId: string }>();
  const navigate = useNavigate();
  const [session, setSession] = useState<PersistedExternalSession | null>(() => readSession());
  const [sessionExpired, setSessionExpired] = useState(false);

  // Redirect anonymous reviewers back to the magic-link landing page so they
  // can request a fresh OTP. Preserve nothing — we don't have a token to
  // hand back.
  useEffect(() => {
    if (!session) {
      navigate('/external/grant', { replace: true });
    }
  }, [session, navigate]);

  const claims = useMemo(
    () => (session ? decodeClaims(session.session_token) : null),
    [session],
  );

  const binderQ = useQuery({
    queryKey: ['external-binder', binderId, session?.session_token],
    queryFn: () => externalAccess.getBinder(binderId, session!.session_token),
    enabled: !!session && !!binderId && !sessionExpired,
    retry: false,
  });

  // Centralised 401/403 handling — clear the session + show the expired screen.
  // BE returns 403 for cross-binder access; we treat both as terminal.
  useEffect(() => {
    if (!binderQ.isError) return;
    const err = binderQ.error;
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      try {
        localStorage.removeItem(EXTERNAL_SESSION_KEY);
      } catch {
        /* ignore */
      }
      setSession(null);
      setSessionExpired(true);
    }
  }, [binderQ.isError, binderQ.error]);

  const folders = binderQ.data?.folders ?? [];
  const sortedFolders = useMemo(
    () => [...folders].sort((a, b) => a.sort_order - b.sort_order),
    [folders],
  );

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  useEffect(() => {
    if (selectedFolderId) return;
    const first = sortedFolders[0];
    if (first) setSelectedFolderId(first.folder_id);
  }, [sortedFolders, selectedFolderId]);

  const selectedFolder = useMemo(
    () => folders.find((f) => f.folder_id === selectedFolderId) ?? null,
    [folders, selectedFolderId],
  );

  function signOut() {
    try {
      localStorage.removeItem(EXTERNAL_SESSION_KEY);
    } catch {
      /* ignore */
    }
    setSession(null);
    navigate('/external/grant', { replace: true });
  }

  if (sessionExpired) {
    return (
      <Shell>
        <div className="rounded border border-surface-border bg-surface-card p-8 text-center">
          <ShieldAlert className="mx-auto mb-3 h-10 w-10 text-warning-600" />
          <h1 className="text-base font-semibold text-ink-primary">Your session has expired</h1>
          <p className="mt-2 text-xs text-ink-muted">
            For security, external review sessions are short-lived. Re-open the link from your
            email to start a new session.
          </p>
          <button
            type="button"
            onClick={() => navigate('/external/grant', { replace: true })}
            className="mt-5 inline-flex items-center justify-center rounded bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700"
          >
            Restart
          </button>
        </div>
      </Shell>
    );
  }

  if (!session) {
    // Effect above is redirecting; render nothing to avoid a flash.
    return null;
  }

  if (binderQ.isLoading) {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 p-12 text-ink-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading binder…
        </div>
      </Shell>
    );
  }

  if (binderQ.isError || !binderQ.data) {
    const message =
      errorText(binderQ.error, 'Failed to load this binder.');
    return (
      <Shell>
        <div className="flex items-start gap-2 rounded border border-danger-200 bg-danger-50 p-4 text-sm text-danger-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{message}</span>
        </div>
      </Shell>
    );
  }

  if (binderQ.data.blinded) {
    return (
      <Shell>
        <div className="flex flex-col items-center gap-3 rounded border border-amber-200 bg-amber-50 p-10 text-center">
          <ShieldAlert className="h-8 w-8 text-amber-600" />
          <h1 className="text-base font-semibold text-amber-900">Content hidden — you're blinded on this study</h1>
          <p className="max-w-md text-xs text-amber-800">
            This grant was issued for a blinded reviewer. The binder's contents are not accessible
            from this session.
          </p>
        </div>
      </Shell>
    );
  }

  const { binder: binderRow } = binderQ.data;
  const binderShort = binderRow.binder_id.slice(0, 8);
  // valid_until is the JWT's exp claim (seconds since epoch) — we render it
  // as a localised date-time so the reviewer always knows when the window closes.
  const validUntil = claims?.exp ? new Date(claims.exp * 1000).toISOString() : null;

  return (
    <Shell>
      {/* Header */}
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-surface-border bg-surface-card px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[22px] font-semibold tracking-tight text-ink-primary">
            <FolderArchive className="h-5 w-5 text-ink-muted" />
            <span>External review — {binderShort}</span>
          </div>
          {claims?.granted_to_email && (
            <p className="mt-0.5 text-[11px] text-ink-muted">
              Signed in as {claims.granted_to_email}
              {claims.granted_to_role ? ` · ${claims.granted_to_role.replace(/_/g, ' ')}` : ''}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="badge badge-info capitalize">{scopeLabel(claims?.scope)}</span>
          {validUntil && (
            <span className="rounded border border-surface-border bg-surface-muted px-2 py-0.5 text-[11px] text-ink-muted">
              Valid until {fmtDateTime(validUntil)}
            </span>
          )}
          <button
            type="button"
            onClick={signOut}
            className="inline-flex items-center gap-1 rounded border border-surface-border bg-surface-card px-2.5 py-1 text-xs text-ink-secondary hover:bg-surface-muted"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </header>

      {/* Disclaimer banner */}
      <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
        <span className="font-semibold">External read-only view.</span> Documents marked PHI may be
        redacted for your role. All views are logged.
      </div>

      {/* Two-column workspace */}
      <div className="flex flex-1 gap-4 p-4">
        {/* Folder tree */}
        <aside className="w-1/3 min-w-0 shrink-0">
          <div className="card flex h-full min-h-0 flex-col overflow-hidden">
            <div className="border-b border-surface-border bg-surface-muted/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
              Folders
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {sortedFolders.length === 0 ? (
                <div className="p-4 text-center text-xs text-ink-muted">
                  No folders are accessible under this grant.
                </div>
              ) : (
                <ExternalFolderTree
                  folders={sortedFolders}
                  selectedFolderId={selectedFolderId}
                  onSelect={setSelectedFolderId}
                />
              )}
            </div>
          </div>
        </aside>

        {/* Document table */}
        <section className="min-w-0 flex-1">
          {selectedFolder && session ? (
            <ExternalDocumentTable
              binderId={binderId}
              sessionToken={session.session_token}
              folder={selectedFolder}
              onAuthError={() => {
                try {
                  localStorage.removeItem(EXTERNAL_SESSION_KEY);
                } catch {
                  /* ignore */
                }
                setSession(null);
                setSessionExpired(true);
              }}
            />
          ) : (
            <div className="card flex h-full items-center justify-center p-8 text-sm text-ink-muted">
              Select a folder to view its documents.
            </div>
          )}
        </section>
      </div>
    </Shell>
  );
}

// =====================================================================
// Folder tree (read-only — no callbacks back into a coordinator API)
// =====================================================================

interface ExternalFolderTreeProps {
  folders: BinderFolderRow[];
  selectedFolderId: string | null;
  onSelect: (folderId: string) => void;
}

interface SectionGroup {
  name: string;
  folders: BinderFolderRow[];
}
interface ZoneGroup {
  name: string;
  sections: SectionGroup[];
}

function groupFolders(folders: BinderFolderRow[]): ZoneGroup[] {
  const zoneMap = new Map<string, Map<string, BinderFolderRow[]>>();
  for (const f of folders) {
    let sections = zoneMap.get(f.zone_name);
    if (!sections) {
      sections = new Map();
      zoneMap.set(f.zone_name, sections);
    }
    let bucket = sections.get(f.section_name);
    if (!bucket) {
      bucket = [];
      sections.set(f.section_name, bucket);
    }
    bucket.push(f);
  }
  return Array.from(zoneMap.entries()).map(([zoneName, sectionMap]) => ({
    name: zoneName,
    sections: Array.from(sectionMap.entries()).map(([sectionName, rows]) => ({
      name: sectionName,
      folders: rows,
    })),
  }));
}

function ExternalFolderTree({ folders, selectedFolderId, onSelect }: ExternalFolderTreeProps) {
  const zones = useMemo(() => groupFolders(folders), [folders]);

  const selectedZoneName = useMemo(() => {
    if (!selectedFolderId) return null;
    return folders.find((f) => f.folder_id === selectedFolderId)?.zone_name ?? null;
  }, [folders, selectedFolderId]);
  const selectedSectionKey = useMemo(() => {
    if (!selectedFolderId) return null;
    const hit = folders.find((f) => f.folder_id === selectedFolderId);
    return hit ? `${hit.zone_name}__${hit.section_name}` : null;
  }, [folders, selectedFolderId]);

  const [openZones, setOpenZones] = useState<Set<string>>(
    () => new Set(selectedZoneName ? [selectedZoneName] : []),
  );
  const [openSections, setOpenSections] = useState<Set<string>>(
    () => new Set(selectedSectionKey ? [selectedSectionKey] : []),
  );

  useEffect(() => {
    if (selectedZoneName) {
      setOpenZones((prev) =>
        prev.has(selectedZoneName) ? prev : new Set(prev).add(selectedZoneName),
      );
    }
    if (selectedSectionKey) {
      setOpenSections((prev) =>
        prev.has(selectedSectionKey) ? prev : new Set(prev).add(selectedSectionKey),
      );
    }
  }, [selectedZoneName, selectedSectionKey]);

  return (
    <nav aria-label="Binder folders" className="text-sm">
      <ul className="space-y-0.5">
        {zones.map((zone) => {
          const zoneOpen = openZones.has(zone.name);
          const zoneCount = zone.sections.reduce((n, s) => n + s.folders.length, 0);
          return (
            <li key={zone.name}>
              <button
                type="button"
                onClick={() =>
                  setOpenZones((prev) => {
                    const next = new Set(prev);
                    if (next.has(zone.name)) next.delete(zone.name);
                    else next.add(zone.name);
                    return next;
                  })
                }
                aria-expanded={zoneOpen}
                className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-[12.5px] font-semibold text-ink-primary hover:bg-surface-muted"
              >
                {zoneOpen ? (
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
                )}
                <span className="truncate">{zone.name}</span>
                <span className="ml-auto text-[10.5px] font-normal text-ink-muted">{zoneCount}</span>
              </button>

              {zoneOpen && (
                <ul className="ml-3 mt-0.5 space-y-0.5 border-l border-surface-border pl-2">
                  {zone.sections.map((section) => {
                    const sectionKey = `${zone.name}__${section.name}`;
                    const sectionOpen = openSections.has(sectionKey);
                    return (
                      <li key={sectionKey}>
                        <button
                          type="button"
                          onClick={() =>
                            setOpenSections((prev) => {
                              const next = new Set(prev);
                              if (next.has(sectionKey)) next.delete(sectionKey);
                              else next.add(sectionKey);
                              return next;
                            })
                          }
                          aria-expanded={sectionOpen}
                          className="flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-[12px] font-medium text-ink-secondary hover:bg-surface-muted"
                        >
                          {sectionOpen ? (
                            <ChevronDown className="h-3 w-3 shrink-0 text-ink-muted" />
                          ) : (
                            <ChevronRight className="h-3 w-3 shrink-0 text-ink-muted" />
                          )}
                          <span className="truncate">{section.name}</span>
                          <span className="ml-auto text-[10.5px] font-normal text-ink-muted">
                            {section.folders.length}
                          </span>
                        </button>

                        {sectionOpen && (
                          <ul className="ml-3 mt-0.5 space-y-0.5 border-l border-surface-border pl-2">
                            {section.folders.map((f) => {
                              const selected = f.folder_id === selectedFolderId;
                              const label = folderDisplayName(f);
                              return (
                                <li key={f.folder_id}>
                                  <button
                                    type="button"
                                    onClick={() => onSelect(f.folder_id)}
                                    aria-current={selected ? 'true' : undefined}
                                    title={label}
                                    className={`group flex w-full items-center gap-1.5 rounded px-1.5 py-1 text-left text-[12px] leading-tight ${
                                      selected
                                        ? 'bg-accent-50 font-medium text-accent'
                                        : 'text-ink-secondary hover:bg-surface-muted'
                                    }`}
                                  >
                                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-ink-muted/40" />
                                    <span className="truncate">{label}</span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

// =====================================================================
// Document table (read-only, no upload / status-edit controls)
// =====================================================================

interface ExternalDocumentTableProps {
  binderId: string;
  sessionToken: string;
  folder: BinderFolderRow;
  onAuthError: () => void;
}

function ExternalDocumentTable({
  binderId,
  sessionToken,
  folder,
  onAuthError,
}: ExternalDocumentTableProps) {
  const folderLabel = folderDisplayName(folder);
  // Sprint-18 — clickable doc titles open the external viewer modal.
  const [viewerDocId, setViewerDocId] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['external-binder', binderId, 'folder', folder.folder_id, 'docs', sessionToken],
    queryFn: () => externalAccess.listDocs(binderId, folder.folder_id, sessionToken),
    enabled: !!binderId && !!folder.folder_id && !!sessionToken,
    retry: false,
  });

  useEffect(() => {
    if (!q.isError) return;
    const err = q.error;
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      onAuthError();
    }
  }, [q.isError, q.error, onAuthError]);

  const documents = q.data?.documents ?? [];
  const currentVersions: Record<string, ExternalDocumentVersionRow | null> =
    q.data?.current_versions ?? {};

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-base font-semibold text-ink-primary">{folderLabel}</h2>
        <p className="text-xs text-ink-muted">
          {q.isLoading
            ? 'Loading documents…'
            : documents.length === 0
              ? 'No documents filed in this folder.'
              : `${documents.length} document${documents.length === 1 ? '' : 's'}`}
        </p>
      </div>

      <div className="card overflow-hidden">
        {q.isLoading && (
          <div className="flex items-center justify-center gap-2 p-8 text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading documents…
          </div>
        )}
        {q.isError && !(q.error instanceof ApiError && (q.error.status === 401 || q.error.status === 403)) && (
          <div className={`flex items-start gap-2 p-4 text-sm ${errorToneClass(q.error)}`}>
            {isPermissionError(q.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
            <span>
              {errorText(q.error, 'Failed to load documents.')}
            </span>
          </div>
        )}
        {q.data && documents.length === 0 && (
          <div className="p-8 text-center text-sm text-ink-muted">
            <FileText className="mx-auto mb-2 h-6 w-6 opacity-50" />
            No documents in this folder.
          </div>
        )}
        <ExternalDocumentViewerModal
          binderId={binderId}
          documentId={viewerDocId ?? ''}
          sessionToken={sessionToken}
          open={!!viewerDocId}
          onClose={() => setViewerDocId(null)}
        />
        {q.data && documents.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b border-surface-border bg-surface-muted text-left text-xs uppercase tracking-wide text-ink-muted">
                <tr>
                  <th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Current version</th>
                  <th className="px-3 py-2">Uploaded</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((d) => {
                  const v = currentVersions[d.document_id] ?? null;
                  const isRedacted = d.title === '[PHI-REDACTED]';
                  return (
                    <tr
                      key={d.document_id}
                      className="border-b border-surface-border last:border-0"
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 shrink-0 text-ink-muted" />
                          <div className="min-w-0">
                            <div className="truncate font-medium">
                              {isRedacted ? (
                                <span className="italic text-ink-muted">PHI redacted</span>
                              ) : (
                                <button
                                  type="button"
                                  className="text-left text-accent-700 hover:text-accent-800 hover:underline"
                                  onClick={() => setViewerDocId(d.document_id)}
                                  title="Open document preview"
                                >
                                  {d.title}
                                </button>
                              )}
                            </div>
                            <div className="truncate text-[10px] text-ink-muted">
                              {d.document_type_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <span className="badge badge-neutral capitalize">
                          {d.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {v ? <span className="font-mono">v{v.version_label}</span> : <span className="text-ink-muted">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-ink-muted">
                        {v ? fmtDateTime(v.uploaded_at) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// Shell (no AppShell — fully standalone, unauthenticated route)
// =====================================================================

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col bg-surface-base">
      {children}
      <footer className="mt-auto border-t border-surface-border bg-surface-card px-4 py-2 text-center text-[11px] text-ink-muted">
        Authorized external access. All views are logged.
      </footer>
    </div>
  );
}
